import React from 'react';
import { FaUserEdit, FaTrash, FaShieldAlt, FaUser, FaCalendarAlt } from 'react-icons/fa';

const UserTable = ({ users, onEdit, onDelete, currentUserId }) => {
  if (!users || users.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
        <p className="text-sm font-semibold text-slate-700">No users found</p>
        <p className="text-xs text-slate-400 mt-1">Try adjusting your search criteria or add a new user.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xs">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-xs">
          <thead className="border-b border-slate-100 bg-slate-50/75 text-slate-500 uppercase tracking-wider font-semibold">
            <tr>
              <th className="px-5 py-3.5">User Profile</th>
              <th className="px-5 py-3.5">Email Address</th>
              <th className="px-5 py-3.5">System Role</th>
              <th className="px-5 py-3.5">Member Since</th>
              <th className="px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((user) => {
              const initials = (user.name || 'User')
                .split(' ')
                .map((n) => n[0])
                .slice(0, 2)
                .join('')
                .toUpperCase();

              const isSelf = user._id === currentUserId;

              return (
                <tr key={user._id} className="hover:bg-slate-50/60 transition">
                  {/* Name + Avatar */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-bold text-white shadow-2xs ${
                          user.isAdmin
                            ? 'bg-gradient-to-br from-[#01295c] to-[#2596be]'
                            : 'bg-gradient-to-br from-slate-600 to-slate-700'
                        }`}
                      >
                        {initials}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-slate-900 text-sm">{user.name}</p>
                          {isSelf && (
                            <span className="rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-700 border border-blue-200">
                              You
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-400">ID: {user._id?.slice(-6)}</p>
                      </div>
                    </div>
                  </td>

                  {/* Email */}
                  <td className="px-5 py-3.5 font-medium text-slate-700">
                    {user.email}
                  </td>

                  {/* Role */}
                  <td className="px-5 py-3.5">
                    {user.isAdmin ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200/80 px-2.5 py-1 text-[11px] font-bold text-amber-900 shadow-2xs">
                        <FaShieldAlt className="text-amber-500 text-[10px]" />
                        <span>Administrator</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                        <FaUser className="text-slate-400 text-[10px]" />
                        <span>Standard User</span>
                      </span>
                    )}
                  </td>

                  {/* Joined Date */}
                  <td className="px-5 py-3.5 text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <FaCalendarAlt className="text-slate-400 text-[10px]" />
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => onEdit(user)}
                        title="Edit User"
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 transition shadow-2xs cursor-pointer"
                      >
                        <FaUserEdit className="text-xs" />
                      </button>

                      <button
                        type="button"
                        onClick={() => onDelete(user)}
                        disabled={isSelf}
                        title={isSelf ? 'Cannot delete your own account' : 'Delete User'}
                        className={`flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white transition shadow-2xs ${
                          isSelf
                            ? 'opacity-30 cursor-not-allowed text-slate-400'
                            : 'text-red-600 hover:border-red-200 hover:bg-red-50 cursor-pointer'
                        }`}
                      >
                        <FaTrash className="text-xs" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserTable;
